import React, { useState } from 'react';
import { Shield, CreditCard, FileText, Phone } from 'lucide-react';
import { InsurancePopup } from './InsurancePopup';

export const InsuranceSection = () => {
  const [showInsurancePopup, setShowInsurancePopup] = useState(false);

  const featuredCompanies = [
    'Care Health Insurance',
    'ICICI Lombard',
    'SBI General',
    'HDFC Ergo',
    'Bajaj Allianz',
    'Star Health'
  ];

  return (
    <>
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Insurance</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Insurance Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We accept all major insurance providers with both cashless and reimbursement options available.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Cashless Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-blue-100">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Cashless</h3>
                  <p className="text-blue-600 font-medium">Direct Settlement</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                No upfront payment required. Your insurance company settles the bills directly with us.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Pre-authorization process</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">No financial stress</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Quick approval system</span>
                </li>
              </ul>
              <button
                onClick={() => setShowInsurancePopup(true)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Learn More About Cashless
              </button>
            </div>

            {/* Reimbursement Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-green-100">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Reimbursement</h3>
                  <p className="text-green-600 font-medium">Pay First, Claim Later</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Pay hospital bills directly and get reimbursed from your insurance company later.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">Flexible payment options</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">Works with all insurers</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">Document assistance provided</span>
                </li>
              </ul>
              <button
                onClick={() => setShowInsurancePopup(true)}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Learn More About Reimbursement
              </button>
            </div>
          </div>

          {/* Featured Companies */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Featured Insurance Partners
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              {featuredCompanies.map((company, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow border"
                >
                  <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-700 text-center block">
                    {company}
                  </span>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => setShowInsurancePopup(true)}
              className="inline-flex items-center space-x-2 bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold"
            >
              <Shield className="w-5 h-5" />
              <span>View All Insurance Companies</span>
            </button>
          </div>

          {/* Contact Info */}
          <div className="text-center mt-12 p-6 bg-white rounded-2xl shadow-sm border">
            <h4 className="font-semibold text-gray-900 mb-4">Need Help with Insurance?</h4>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">02067527445</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">7066901355</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">7559467108</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Popup */}
      {showInsurancePopup && (
        <InsurancePopup onClose={() => setShowInsurancePopup(false)} />
      )}
    </>
  );
};